/**
 * Pure auto-start eligibility for the welcome tour. Every input is a plain
 * value so the rule is unit-testable condition by condition; the provider
 * gathers the inputs and reacts to the verdict.
 */
import { TOUR_VERSION, MIN_TARGETED_STEPS, TEACHER_DASHBOARD_PATH } from './constants';

export const INELIGIBLE = Object.freeze({
  flagOff: 'flag-off',
  profileNotLoaded: 'profile-not-loaded',
  role: 'role',
  noClasses: 'no-classes',
  notDashboard: 'not-dashboard',
  notReady: 'dashboard-not-ready',
  seen: 'tour-version-current',
  modalOpen: 'modal-open',
  tourActive: 'tour-active',
  tooFewTargets: 'too-few-targets',
});

/**
 * Seen = the profile carries tourVersion >= TOUR_VERSION. The start marker
 * (recordTourStarted) stamps tourVersion the moment the first step renders,
 * so a teacher who closes the tab mid-tour counts as seen; Done / Skip stamp
 * it again with their own timestamp. Absent / non-numeric → not seen.
 */
export function hasSeenTour(onboarding) {
  const v = onboarding && typeof onboarding.tourVersion === 'number' ? onboarding.tourVersion : 0;
  return v >= TOUR_VERSION;
}

/**
 * "Profile loaded" is App.js's own signal: `user.profile` is the users/{uid}
 * doc data once the boot getDoc resolved (an object), `null` when the doc
 * does not exist, `undefined` while it has not been read. Only the object
 * case may auto-start anything: "not loaded yet" and "no doc" are never
 * confused with "loaded, no onboarding field" (the once-only guarantee's
 * ordering, made explicit — see README "Once-only guarantee").
 */
export function isProfileLoaded(user) {
  return !!(user && user.profile && typeof user.profile === 'object');
}

export function hasSeenHint(onboarding, key) {
  return !!(onboarding && onboarding.hints && onboarding.hints[key] && onboarding.hints[key].seenAt);
}

/**
 * @returns {{ eligible: boolean, reason: string|null }}
 */
export function evaluateAutoStart({
  flagOn,
  profileLoaded,
  primaryRole,
  classCount,
  pathname,
  dashboardReady,
  onboarding,
  modalOpen,
  tourActive,
  targetedStepCount,
}) {
  if (!flagOn) return { eligible: false, reason: INELIGIBLE.flagOff };
  if (!profileLoaded) return { eligible: false, reason: INELIGIBLE.profileNotLoaded };
  if (primaryRole !== 'teacher') return { eligible: false, reason: INELIGIBLE.role };
  if (pathname !== TEACHER_DASHBOARD_PATH) return { eligible: false, reason: INELIGIBLE.notDashboard };
  if (!dashboardReady) return { eligible: false, reason: INELIGIBLE.notReady };
  if (!(classCount > 0)) return { eligible: false, reason: INELIGIBLE.noClasses };
  if (hasSeenTour(onboarding)) return { eligible: false, reason: INELIGIBLE.seen };
  if (modalOpen) return { eligible: false, reason: INELIGIBLE.modalOpen };
  if (tourActive) return { eligible: false, reason: INELIGIBLE.tourActive };
  if (typeof targetedStepCount === 'number' && targetedStepCount < MIN_TARGETED_STEPS) {
    return { eligible: false, reason: INELIGIBLE.tooFewTargets };
  }
  return { eligible: true, reason: null };
}
